import client from 'client'
import type {
  GetStreamingConfigurationStreamingGetQueryResponse,
  GetStreamingConfigurationStreamingGetQueryParams,
  GetStreamingConfigurationStreamingGet422,
} from '../../types/GetStreamingConfigurationStreamingGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getStreamingConfigurationStreamingGetQueryKey = (params?: GetStreamingConfigurationStreamingGetQueryParams) =>
  [{ url: '/streaming' }, ...(params ? [params] : [])] as const

export type GetStreamingConfigurationStreamingGetQueryKey = ReturnType<typeof getStreamingConfigurationStreamingGetQueryKey>

/**
 * @description <h3>ALPHA</h3><p>Gets the streaming configuration for the company or specifiedasset position.</p>
 * @summary Get Streaming Configuration
 * {@link /streaming}
 */
export async function getStreamingConfigurationStreamingGet(
  params?: GetStreamingConfigurationStreamingGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<GetStreamingConfigurationStreamingGetQueryResponse, ResponseErrorConfig<GetStreamingConfigurationStreamingGet422>, unknown>({
    method: 'GET',
    url: `/streaming`,
    params,
    ...requestConfig,
  })
  return res
}

export function getStreamingConfigurationStreamingGetQueryOptions(
  params?: GetStreamingConfigurationStreamingGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const queryKey = getStreamingConfigurationStreamingGetQueryKey(params)
  return queryOptions<
    ResponseConfig<GetStreamingConfigurationStreamingGetQueryResponse>,
    ResponseErrorConfig<GetStreamingConfigurationStreamingGet422>,
    ResponseConfig<GetStreamingConfigurationStreamingGetQueryResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getStreamingConfigurationStreamingGet(params, config)
    },
  })
}

/**
 * @description <h3>ALPHA</h3><p>Gets the streaming configuration for the company or specifiedasset position.</p>
 * @summary Get Streaming Configuration
 * {@link /streaming}
 */
export function useGetStreamingConfigurationStreamingGet<
  TData = ResponseConfig<GetStreamingConfigurationStreamingGetQueryResponse>,
  TQueryData = ResponseConfig<GetStreamingConfigurationStreamingGetQueryResponse>,
  TQueryKey extends QueryKey = GetStreamingConfigurationStreamingGetQueryKey,
>(
  params?: GetStreamingConfigurationStreamingGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetStreamingConfigurationStreamingGetQueryResponse>,
        ResponseErrorConfig<GetStreamingConfigurationStreamingGet422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getStreamingConfigurationStreamingGetQueryKey(params)

  const query = useQuery({
    ...(getStreamingConfigurationStreamingGetQueryOptions(params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetStreamingConfigurationStreamingGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}